// src/utils/fetchPivotOptions.js
import {
  collection, doc, getDoc, getDocs,
  query, orderBy, limit
} from 'firebase/firestore';

export const MAIN_QUESTIONS_HEADER = 'sys_mainQuestions';

const safeTrim = (value) => (typeof value === 'string' ? value.trim() : '');

export const extractMainQuestionsPayload = (colTypes = []) => {
  if (!Array.isArray(colTypes) || !colTypes.length) return null;
  for (const col of colTypes) {
    if (!col || typeof col !== 'object') continue;
    const header = safeTrim(col.header ?? col.id);
    if (!header || header !== MAIN_QUESTIONS_HEADER) continue;
    const payload = col.main_questions || col.mainQuestions;
    if (payload && typeof payload === 'object') {
      return payload;
    }
  }
  return null;
};

const buildOptionsFromMainQuestions = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return {
      rowGroupOptions: [],
      leafColumnOptions: [],
      displayCols: [],
    };
  }

  const normaliseColumns = (cols = []) =>
    (Array.isArray(cols) ? cols : [])
      .map((col) => {
        if (!col || typeof col !== 'object') return null;
        const header = safeTrim(col.header) || safeTrim(col.id);
        if (!header) return null;
        const id = safeTrim(col.id) || header;
        return { ...col, header, id };
      })
      .filter(Boolean);

  const orderedGroups = (Array.isArray(payload.groups) ? payload.groups : [])
    .filter((group) => group && typeof group === 'object')
    .slice()
    .sort((a, b) => {
      const ao = typeof a.order === 'number' && Number.isFinite(a.order) ? a.order : Number.MAX_SAFE_INTEGER;
      const bo = typeof b.order === 'number' && Number.isFinite(b.order) ? b.order : Number.MAX_SAFE_INTEGER;
      return ao - bo;
    });

  const orderedUngrouped = (Array.isArray(payload.ungrouped) ? payload.ungrouped : [])
    .filter((col) => col && typeof col === 'object')
    .slice()
    .sort((a, b) => {
      const ao = typeof a.order === 'number' && Number.isFinite(a.order) ? a.order : Number.MAX_SAFE_INTEGER;
      const bo = typeof b.order === 'number' && Number.isFinite(b.order) ? b.order : Number.MAX_SAFE_INTEGER;
      return ao - bo;
    });

  const displayCols = [];
  const rowGroupSet = new Set();
  const leafMap = new Map();

  const registerLeaf = (col) => {
    if (!col || typeof col !== 'object') return;
    const header = safeTrim(col.header) || safeTrim(col.id);
    if (!header) return;
    const id = safeTrim(col.id) || header;
    if (!leafMap.has(id)) {
      leafMap.set(id, { id, header });
    }
  };

  orderedGroups.forEach((group) => {
    const baseName =
      safeTrim(group?.name) ||
      safeTrim(group?.id) ||
      safeTrim(group?.label);
    if (baseName) {
      rowGroupSet.add(baseName);
    }
    const columns = normaliseColumns(group?.columns);
    columns.forEach(registerLeaf);
    const headerCandidate =
      baseName ||
      safeTrim(columns[0]?.groupname) ||
      (columns[0]?.header ?? '');
    const header = safeTrim(headerCandidate);
    if (header) {
      rowGroupSet.add(header);
    }
    const groupId = safeTrim(group?.id) || header || baseName;
    if (!groupId && !header) {
      return;
    }
    displayCols.push({
      ...group,
      header: header || groupId || '',
      id: groupId || header || '',
      name: safeTrim(group?.name) || header || groupId || '',
      columns,
    });
  });

  orderedUngrouped.forEach((col) => {
    const normalised = normaliseColumns([col])[0];
    if (!normalised) return;
    rowGroupSet.add(normalised.header);
    registerLeaf(normalised);
    displayCols.push(normalised);
  });

  const rowGroupOptions = Array.from(rowGroupSet)
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, 'tr', { sensitivity: 'base' }));

  const leafColumnOptions = Array.from(leafMap.values()).sort((a, b) =>
    a.header.localeCompare(b.header, 'tr', { sensitivity: 'base' })
  );

  return { rowGroupOptions, leafColumnOptions, displayCols };
};

const buildLegacyOptions = (colTypes = []) => {
  const deriveGroup = (col) => {
    if (col && col.manualUngroup) return col.header || col.id || '';

    const head = (col && (col.header || col.id)) || '';
    const id = col && col.id ? col.id : '';
    let gt = ((col && (col.grouptype || '')) + '').toLowerCase();

    if (gt === 'multigriddata') {
      const mHead = head.match(/^([A-Za-z0-9]+)_r(\d+)_c(\d+)$/i);
      if (mHead) return `${mHead[1]}_r${mHead[2]}`;
      const mId = id.match(/^([A-Za-z0-9]+)_r(\d+)_c(\d+)$/i);
      if (mId) return `${mId[1]}_r${mId[2]}`;
      return head;
    }

    if (gt === 'griddata') {
      return head;
    }

    if (gt === 'multiplechoice') {
      if (col && col.groupname) return col.groupname;
      return head;
    }

    return head;
  };

  const rowGroupOptions = (() => {
    const set = new Set();
    (colTypes || []).forEach((c) => {
      if (!c || c.active === false) return;
      const label = deriveGroup(c);
      if (label) set.add(String(label));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'tr'));
  })();

  const leafColumnOptions = (() => {
    const arr = (colTypes || [])
      .filter(Boolean)
      .map((c) => ({ id: c.id || c.header, header: c.header || c.id }));
    arr.sort((a, b) => String(a.header).localeCompare(String(b.header), 'tr'));
    return arr;
  })();

  const displayCols = (() => {
    const groupSet = new Set();

    const baseCols = (colTypes || []).filter((c) => {
      if (!c) return false;
      const grp = deriveGroup(c);
      const headOrId = c.header || c.id;
      const isAlt = !c.manualUngroup && grp !== headOrId;
      if (isAlt) groupSet.add(grp);
      return !isAlt;
    });

    const virtualCols = [...groupSet]
      .filter((g) => !baseCols.some((c) => (c.header || c.id) === g))
      .map((g) => {
        const members = (colTypes || []).filter((c) => !c.manualUngroup && deriveGroup(c) === g);
        const opts = [
          ...new Set(
            members.flatMap((m) =>
              (m.options || []).filter((o) => String(o).trim() && o !== '0')
            )
          ),
        ];
        return {
          header: g,
          type: 'multi-select data',
          grouptype: 'multiplechoice_group',
          options: opts,
        };
      });

    return [...baseCols, ...virtualCols].sort((a, b) =>
      (a.header || '').localeCompare(b.header || '', 'tr', { sensitivity: 'base' })
    );
  })();

  return { rowGroupOptions, leafColumnOptions, displayCols };
};

/**
 * Firestore'dan pivot seçimleri için kolon verisini yükler.
 * Öncelik: graphDatasets (FULL payload) -> analysis doc -> project.rawData
 *
 * @param {Object} params
 * @param {import('firebase/firestore').Firestore} params.db
 * @param {string} params.organisation
 * @param {string} params.projectID
 * @param {string} [params.analysisID]
 * @returns {Promise<{
 *   colTypes: Array,
 *   rowGroupOptions: string[],
 *   leafColumnOptions: {id:string, header:string}[],
 *   displayCols: Array
 * }>}
 */
export async function fetchPivotOptions({ db, organisation, projectID, analysisID }) {
  if (!db || !organisation || !projectID) {
    return { colTypes: [], rowGroupOptions: [], leafColumnOptions: [], displayCols: [] };
  }

  // 1) En güncel FULL payload: graphDatasets
  const basePath = analysisID
    ? ['organisations', organisation, 'analyses', analysisID]
    : ['organisations', organisation, 'projects', projectID];
  const datasetsColl = collection(db, ...basePath, 'graphDatasets');

  let colTypes = [];

  try {
    let qs = await getDocs(query(datasetsColl, orderBy('createdAt', 'desc'), limit(1)));
    if (qs.empty) {
      // Eski belgelerde createDate kullanılmış olabilir
      qs = await getDocs(query(datasetsColl, orderBy('createDate', 'desc'), limit(1)));
    }
    if (!qs.empty) {
      const data = qs.docs[0].data() || {};
      if (Array.isArray(data.columnTypes) && data.columnTypes.length) {
        colTypes = data.columnTypes;
      }
    }
  } catch (e) {
    console.warn('[fetchPivotOptions] graphDatasets okunamadı:', e);
  }

  // 2) Fallback: analysis dokümanı içinde columnTypes var mı?
  if (!colTypes.length && analysisID) {
    try {
      const aRef = doc(db, 'organisations', organisation, 'analyses', analysisID);
      const aSnap = await getDoc(aRef);
      const aData = aSnap.exists() ? (aSnap.data() || {}) : {};
      if (Array.isArray(aData.columnTypes) && aData.columnTypes.length) {
        colTypes = aData.columnTypes;
      }
    } catch (e) {
      console.warn('[fetchPivotOptions] analysis columnTypes okunamadı:', e);
    }
  }

  // 3) Her durumda proje rawData'sını da al ve birleştir
  let projectRaw = [];
  try {
    const pRef = doc(db, 'organisations', organisation, 'projects', projectID);
    const pSnap = await getDoc(pRef);
    const pData = pSnap.exists() ? (pSnap.data() || {}) : {};
    if (Array.isArray(pData.rawData) && pData.rawData.length) {
      projectRaw = pData.rawData;
    }
  } catch (e) {
    console.warn('[fetchPivotOptions] project.rawData okunamadı:', e);
  }

  if (projectRaw.length) {
    const byKey = (arr = []) => {
      const m = new Map();
      arr.forEach((c) => {
        if (!c || typeof c !== 'object') return;
        const id = c.id || c.header;
        const header = c.header || c.id;
        const key = String(id || header || '');
        if (!key) return;
        const existing = m.get(key) || {};
        m.set(key, { ...existing, ...c });
      });
      return m;
    };

    const baseMap = byKey(colTypes);
    const mergedMap = byKey(projectRaw);

    // Merge dataset entries first, then overlay fresh project metadata
    const finalMap = new Map(baseMap);
    mergedMap.forEach((val, key) => {
      const existing = finalMap.get(key) || {};
      finalMap.set(key, { ...existing, ...val });
    });

    colTypes = Array.from(finalMap.values());
  }

  const mainQuestionsPayload = extractMainQuestionsPayload(colTypes);
  if (mainQuestionsPayload) {
    const derived = buildOptionsFromMainQuestions(mainQuestionsPayload);
    return { colTypes, mainQuestionsPayload, ...derived };
  }

  const fallback = buildLegacyOptions(colTypes);
  return { colTypes, mainQuestionsPayload: null, ...fallback };
}
